import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, Cpu, Shield, Search, Megaphone, BookOpen, GitBranch, Server, Zap, BrainCircuit, Save, RotateCcw, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ICONS = { 'MOA': BrainCircuit, 'ARIA': Zap, 'CHANGE-IQ': GitBranch, 'ATLAS': Server, 'SENTINL': Shield, 'NOVA': Search, 'CONCIERGE': Bot, 'CAPACITOR': Cpu, 'HERALD': Megaphone, 'LEARN-X': BookOpen };
const COLORS = { 'MOA': '#3B82F6', 'ARIA': '#EF4444', 'CHANGE-IQ': '#F59E0B', 'ATLAS': '#06B6D4', 'SENTINL': '#EF4444', 'NOVA': '#8B5CF6', 'CONCIERGE': '#10B981', 'CAPACITOR': '#F97316', 'HERALD': '#EC4899', 'LEARN-X': '#6366F1' };

export default function AgentConfigPage() {
  const token = localStorage.getItem('nexus_token');
  const queryClient = useQueryClient();
  const [editingAgent, setEditingAgent] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [modelPanel, setModelPanel] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const { data: configs, isLoading } = useQuery({
    queryKey: ['agent-configs'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/agent-config`, { headers }); return data; },
  });

  const { data: models } = useQuery({
    queryKey: ['agent-models'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/agents/models`, { headers }); return data; },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ name, updates }) => {
      const { data } = await axios.patch(`${BACKEND_URL}/api/agent-config/${name}`, updates, { headers });
      return data;
    },
    onSuccess: (data) => { toast.success(`${data.agent_name} config saved`); setEditingAgent(null); queryClient.invalidateQueries(['agent-configs']); },
    onError: () => toast.error('Failed to save'),
  });

  const modelMutation = useMutation({
    mutationFn: async ({ name, model }) => {
      const { data } = await axios.patch(`${BACKEND_URL}/api/agents/models/${name}`, model, { headers });
      return data;
    },
    onSuccess: (data) => { toast.success(`${data.agent} model updated to ${data.model?.model}`); setModelPanel(null); queryClient.invalidateQueries(['agent-models', 'agent-configs']); },
    onError: () => toast.error('Failed to update model'),
  });

  const startEdit = (cfg) => { setEditingAgent(cfg.agent_name); setEditValues({ confidence_threshold: cfg.confidence_threshold, auto_resolve: cfg.auto_resolve, maintenance_mode: cfg.maintenance_mode }); };

  const getAgentModel = (agentName) => {
    return models?.defaults?.[agentName] || { provider: 'anthropic', model: 'claude-4-sonnet-20250514' };
  };

  const getModelLabel = (model) => {
    const labels = {
      'claude-4-sonnet-20250514': 'Claude 4 Sonnet',
      'claude-3-5-haiku-20241022': 'Claude 3.5 Haiku',
      'gpt-4o': 'GPT-4o',
      'gpt-4o-mini': 'GPT-4o Mini',
      'gemini-2.5-flash': 'Gemini 2.5 Flash',
    };
    return labels[model?.model] || model?.model || 'Unknown';
  };

  return (
    <div data-testid="agent-config-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Agent Configuration</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Tune confidence thresholds, LLM models, SLA configs, and tool permissions per agent</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[...Array(5)].map((_, i) => <div key={i} className="h-48 rounded-xl bg-[var(--nexus-bg-elevated)] animate-pulse" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="agent-config-grid">
          {(configs || []).map(cfg => {
            const Icon = ICONS[cfg.agent_name] || Bot;
            const color = COLORS[cfg.agent_name] || '#06B6D4';
            const isEditing = editingAgent === cfg.agent_name;
            const isModelOpen = modelPanel === cfg.agent_name;
            const agentModel = getAgentModel(cfg.agent_name);
            return (
              <div key={cfg.agent_name} className={`nexus-card relative overflow-hidden p-4 ${cfg.maintenance_mode ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}><Icon size={16} /></div>
                    <div>
                      <span className="font-mono text-xs font-bold text-[var(--nexus-text-primary)]">{cfg.agent_name}</span>
                      {cfg.maintenance_mode && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-[#4d3000] text-[#fcd34d] border border-amber-500/30 font-semibold">MAINTENANCE</span>}
                    </div>
                  </div>
                  {!isEditing ? (
                    <button onClick={() => startEdit(cfg)} className="text-[10px] text-[var(--nexus-accent)] hover:underline" data-testid={`edit-${cfg.agent_name}`}>Edit</button>
                  ) : (
                    <div className="flex gap-1">
                      <button onClick={() => saveMutation.mutate({ name: cfg.agent_name, updates: editValues })} className="flex items-center gap-1 px-2 py-1 rounded bg-[#0d3d1d] text-green-300 text-[10px] border border-green-500/30"><Save size={10} /> Save</button>
                      <button onClick={() => setEditingAgent(null)} className="flex items-center gap-1 px-2 py-1 rounded bg-[var(--nexus-bg-base)] text-[var(--nexus-text-muted)] text-[10px] border border-[var(--nexus-border)]"><RotateCcw size={10} /></button>
                    </div>
                  )}
                </div>

                <div className="space-y-2.5">
                  {/* LLM Model Selector */}
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-[var(--nexus-text-muted)]">LLM Model</span>
                      <button onClick={() => setModelPanel(isModelOpen ? null : cfg.agent_name)}
                        data-testid={`model-select-${cfg.agent_name}`}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#0d2847] border border-blue-500/30 text-xs text-[var(--nexus-primary-bright)] font-medium hover:bg-[#0f3058] transition-colors">
                        {getModelLabel(agentModel)}
                        <ChevronDown size={10} className={`transition-transform ${isModelOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    {/* Model dropdown */}
                    {isModelOpen && models?.available && (
                      <div className="mt-2 p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] animate-nexus-slide-in" data-testid={`model-dropdown-${cfg.agent_name}`}>
                        <p className="text-[10px] text-[var(--nexus-text-muted)] mb-2 uppercase tracking-wider font-medium">Select Model</p>
                        <div className="space-y-1">
                          {Object.entries(models.available).map(([provider, providerModels]) =>
                            Object.entries(providerModels).map(([modelId, info]) => {
                              const isSelected = agentModel.model === modelId;
                              return (
                                <button key={modelId} onClick={() => modelMutation.mutate({ name: cfg.agent_name, model: { provider, model: modelId } })}
                                  data-testid={`select-model-${modelId}`}
                                  className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${
                                    isSelected ? 'bg-[#0d2847] border border-blue-500/30' : 'bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] hover:bg-white/[0.04]'
                                  }`}>
                                  <div>
                                    <span className={`text-xs font-semibold ${isSelected ? 'text-[var(--nexus-primary-bright)]' : 'text-[var(--nexus-text-primary)]'}`}>{info.name}</span>
                                    <p className="text-[9px] text-[var(--nexus-text-muted)]">{provider} &bull; {info.tier} &bull; {(info.tokens_per_min / 1000).toFixed(0)}k tok/min</p>
                                  </div>
                                  {isSelected && <span className="text-[10px] text-[var(--nexus-primary-bright)] font-semibold">Active</span>}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confidence Threshold */}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[var(--nexus-text-muted)]">Confidence Threshold</span>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <input type="range" min="40" max="99" value={editValues.confidence_threshold} onChange={e => setEditValues(v => ({...v, confidence_threshold: parseInt(e.target.value)}))} className="w-20 h-1 accent-cyan-400" />
                        <span className="font-mono text-xs text-[var(--nexus-accent)] tabular-nums w-8 text-right">{editValues.confidence_threshold}%</span>
                      </div>
                    ) : (
                      <span className="font-mono text-xs tabular-nums" style={{ color }}>{cfg.confidence_threshold}%</span>
                    )}
                  </div>
                  {/* Auto Resolve */}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[var(--nexus-text-muted)]">Auto Resolve</span>
                    {isEditing ? (
                      <button onClick={() => setEditValues(v => ({...v, auto_resolve: !v.auto_resolve}))} className={`px-2 py-0.5 rounded text-[10px] font-semibold ${editValues.auto_resolve ? 'bg-[#0d3d1d] text-green-300 border border-green-500/30' : 'bg-[var(--nexus-bg-base)] text-[var(--nexus-text-muted)] border border-[var(--nexus-border)]'}`}>
                        {editValues.auto_resolve ? 'Enabled' : 'Disabled'}
                      </button>
                    ) : (
                      <span className={`text-[10px] font-semibold ${cfg.auto_resolve ? 'text-[var(--nexus-success)]' : 'text-[var(--nexus-text-muted)]'}`}>{cfg.auto_resolve ? 'Enabled' : 'Disabled'}</span>
                    )}
                  </div>
                  {/* Maintenance Mode */}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[var(--nexus-text-muted)]">Maintenance Mode</span>
                    {isEditing ? (
                      <button onClick={() => setEditValues(v => ({...v, maintenance_mode: !v.maintenance_mode}))} className={`px-2 py-0.5 rounded text-[10px] font-semibold ${editValues.maintenance_mode ? 'bg-[#4d3000] text-[#fcd34d] border border-amber-500/30' : 'bg-[var(--nexus-bg-base)] text-[var(--nexus-text-muted)] border border-[var(--nexus-border)]'}`}>
                        {editValues.maintenance_mode ? 'ON' : 'OFF'}
                      </button>
                    ) : (
                      <span className={`text-[10px] font-semibold ${cfg.maintenance_mode ? 'text-[var(--nexus-warning)]' : 'text-[var(--nexus-text-muted)]'}`}>{cfg.maintenance_mode ? 'ON' : 'OFF'}</span>
                    )}
                  </div>
                  {/* Tools */}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[var(--nexus-text-muted)]">Tool Permissions</span>
                    <span className="text-[10px] text-[var(--nexus-success)] font-semibold">{cfg.tool_permissions?.all_enabled ? 'All Enabled' : `${cfg.tool_permissions?.disabled_tools?.length || 0} disabled`}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
